import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { createContext, useContext, useState, type ReactNode } from "react";
import { useRemote } from "./remote-context";
// import apiService from "../api/api-service";
// import { contentqaMsalInstance } from "../helpers/msalConfigModule";
import { msalInstance } from "../helpers/msalConfig";
import apiService from "../api/api-service";
import { contentqaMsalInstance } from "../helpers/msalConfigModule";
// import type { sessionDetails } from "@/interfaces/session";
// import type { FileDetails } from "@/api/interfaces/file_list";
// import apiService from "@/api/api-service";

type SessionDetails = {
  work_session_id: string;
  work_group_id: string;
  project_id: string;
  client_id: string;
  user_id: string;
  accessToken: string;
  userDetails: {
    name: string;
    email: string;
    givenName: string;
  };
};

interface SessionContextType {
  sessionDetails: SessionDetails;
  setSessionDetails: React.Dispatch<React.SetStateAction<SessionDetails>>;
  getToken: () => Promise<any>;
  getModuleToken: () => Promise<any>;
  getPlatformToken: () => Promise<any>;
  isJoinedRoom: boolean;
  setIsJoinedRoom: React.Dispatch<React.SetStateAction<boolean>>;
  pageContent: string;
  setPageContent: React.Dispatch<React.SetStateAction<string>>;
  //   selectedFileData: FileDetails | null;
  //   setSelectedFileData: React.Dispatch<React.SetStateAction<FileDetails | null>>;
  roleName: string;
  setRoleName: React.Dispatch<React.SetStateAction<string>>;
  isApproved: boolean;
  setIsApproved: React.Dispatch<React.SetStateAction<boolean>>;
  hasAccessError: boolean;
  setHasAccessError: React.Dispatch<React.SetStateAction<boolean>>;
  moduleAccessToken: string;
  setModuleAccessToken: React.Dispatch<React.SetStateAction<string>>;
  logout: () => Promise<void>;
}

export const SessionProviderContext = createContext<
  SessionContextType | undefined
>(undefined);

export const SessionProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [sessionDetails, setSessionDetails] = useState<SessionDetails>({
    work_session_id: "",
    work_group_id: "",
    project_id: "",
    client_id: "",
    user_id: "",
    accessToken: "",
    userDetails: {
      name: "",
      email: "",
      givenName: "",
    },
  });
  const [moduleAccessToken, setModuleAccessToken] = useState<string>("");
  const [isJoinedRoom, setIsJoinedRoom] = useState<boolean>(false);
  const { instance } = useMsal();
  const { WorkSessionID, WorkGroupID, UserId, ClientId } = useRemote();
  const [pageContent, setPageContent] = useState<string>("dashboard");
  //   const [selectedFileData, setSelectedFileData] = useState<FileDetails | null>(null);
  const [roleName, setRoleName] = useState<string>("");
  const [isApproved, setIsApproved] = useState(false);
  const [hasAccessError, setHasAccessError] = useState(false);

  const getToken = async () => {
    try {
      const authService = await import("mosaic_host_app/authService");
      const tokenResponse = await authService.acquireToken();

      if (tokenResponse?.split(".").length === 3) {
        try {
          const base64Url = tokenResponse.split(".")[1];
          const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
          const jsonPayload = decodeURIComponent(
            atob(base64)
              .split("")
              .map(function (c) {
                return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
              })
              .join(""),
          );
          const userDetails = JSON.parse(jsonPayload);
          setSessionDetails((prev) => {
            return {
              ...prev,
              work_session_id: WorkSessionID ?? "",
              work_group_id: WorkGroupID ?? "",
              user_id: UserId ?? "",
              client_id: ClientId ?? "",
              accessToken: tokenResponse,
              userDetails: {
                name: userDetails.name || "",
                email: userDetails.unique_name || "",
                givenName: userDetails.given_name || "",
              },
            };
          });
          // Check if UserId is available
          if (!UserId) {
            setHasAccessError(true);
            return tokenResponse;
          }
          try {
            const userInfo = await apiService.get(
              `user/${UserId}`,
              {},
              "platformUrl",
              tokenResponse,
            );
            // Set role name from API response
            if (userInfo.data?.rolename) {
              setRoleName(userInfo.data.rolename);
            }
          } catch (userInfoError) {
            console.error("Failed to fetch user info:", userInfoError);
            setHasAccessError(true);
          }
        } catch (e) {
          console.warn("Failed to decode access token:", e);
        }
      }
      return tokenResponse;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        const redirectRequest = {
          account: instance.getAllAccounts()[0],
          scopes: ["https://graph.windows.net/User.Read", "openid"],
        };
        instance.acquireTokenRedirect(redirectRequest);
      } else {
        console.log("Error during initial acquireTokenSilent:", error);
      }
    }
  };

  const getModuleToken = async () => {
    try {
      // Initialize the instance if not already done
      await contentqaMsalInstance.initialize();

      // Handle redirect response first (after user returns from login)
      const redirectResponse =
        await contentqaMsalInstance.handleRedirectPromise();
      if (redirectResponse) {
        console.log("Redirect response received:", redirectResponse);
        contentqaMsalInstance.setActiveAccount(redirectResponse.account);
        setModuleAccessToken(redirectResponse.accessToken);
        return redirectResponse;
      }

      const accounts = contentqaMsalInstance.getAllAccounts();

      // If no accounts, need interactive login first
      if (!accounts || accounts.length === 0) {
        console.log(
          "No accounts found - triggering redirect login for ContentQA",
        );

        // Trigger redirect - function won't return, page will redirect
        await contentqaMsalInstance.loginRedirect({
          scopes: [import.meta.env.VITE_CONTENTQA_SCOPE],
        });

        // Code after redirect won't execute until user comes back
        return;
      }

      // Account exists - use silent token acquisition
      const tokenResponse = await contentqaMsalInstance.acquireTokenSilent({
        account: accounts[0],
        scopes: [import.meta.env.VITE_CONTENTQA_SCOPE],
        forceRefresh: false,
      });

      setModuleAccessToken(tokenResponse.accessToken);
      return tokenResponse;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Token expired or needs refresh - trigger redirect
        console.log("Token expired - triggering redirect for ContentQA");
        await contentqaMsalInstance.acquireTokenRedirect({
          scopes: [import.meta.env.VITE_CONTENTQA_SCOPE],
        });
        return;
      } else {
        console.error("Error getting module token:", error);
        throw error;
      }
    }
  };

  const getPlatformToken = async () => {
    try {
      // Initialize the instance if not already done
      await msalInstance.initialize();

      const accounts = msalInstance.getAllAccounts();

      // If no accounts, use popup for interactive login
      if (!accounts || accounts.length === 0) {
        console.log("No accounts found - triggering popup login for Platform");

        const loginResponse = await msalInstance.loginPopup({
          scopes: ["https://graph.windows.net/User.Read", "openid"],
        });

        msalInstance.setActiveAccount(loginResponse.account);

        // Decode JWT and set session details
        if (loginResponse.accessToken) {
          decodeAndSetUserDetails(loginResponse.accessToken);
        }

        return loginResponse;
      }

      // Account exists - use silent token acquisition
      const tokenResponse = await msalInstance.acquireTokenSilent({
        account: accounts[0],
        scopes: ["https://graph.windows.net/User.Read", "openid"],
        forceRefresh: false,
      });


      // Decode JWT and set session details
      if (tokenResponse.accessToken) {
        console.log(
          "Decoding platform token to set user details",
          tokenResponse,
        );
        decodeAndSetUserDetails(tokenResponse.accessToken);
      }

      return tokenResponse;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Token expired or needs refresh - use popup
        console.log("Token expired - triggering popup for Platform");
        const tokenResponse = await msalInstance.acquireTokenPopup({
          scopes: ["https://graph.windows.net/User.Read", "openid"],
        });

        // Decode JWT and set session details
        if (tokenResponse.accessToken) {
          decodeAndSetUserDetails(tokenResponse.accessToken);
        }

        return tokenResponse;
      } else {
        console.error("Error getting platform token:", error);
        throw error;
      }
    }
  };

  const decodeAndSetUserDetails = async (accessToken: string) => {
    if (accessToken?.split(".").length === 3) {
      try {
        const base64Url = accessToken.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split("")
            .map(function (c) {
              return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join(""),
        );
        const userDetails = JSON.parse(jsonPayload);

        // Fetch user ID and session details from platform init API
        try {
          const authResponse = await apiService.post(
            "init",
            {},
            "platformUrl",
            accessToken,
          );


          setSessionDetails((prev) => {
            return {
              ...prev,
              work_session_id: WorkSessionID ?? "",
              work_group_id: WorkGroupID ?? "",
              user_id: authResponse?.data?.userid ?? "",
              client_id: authResponse?.data?.clientid ?? "",
              accessToken: accessToken,
              userDetails: {
                name: userDetails.name || "",
                email: userDetails.unique_name || "",
                givenName: userDetails.given_name || "",
              },
            };
          });

          // Fetch user info using the user_id from init response
          if (authResponse?.data?.userid) {
            try {
              const userInfo = await apiService.get(
                `user/${authResponse.data.userid}`,
                {},
                "platformUrl",
                accessToken,
              );
              // Set role name from API response
              if (userInfo.data?.rolename) {
                setRoleName(userInfo.data.rolename);
              }
            } catch (userInfoError) {
              console.error("Failed to fetch user info:", userInfoError);
              setHasAccessError(true);
            }
          } else {
            console.log("User ID not available from init response");
            setHasAccessError(true);
          }
        } catch (initError) {
          console.error("Failed to initialize platform session:", initError);
          setHasAccessError(true);
        }
      } catch (e) {
        console.warn("Failed to decode access token:", e);
      }
    }
  };

  const logout = async () => {
    try {
      console.log('[SessionContext] 🚪 Logging out user...');
      
      // Clear session state
      setSessionDetails({
        work_session_id: "",
        work_group_id: "",
        project_id: "",
        client_id: "",
        user_id: "",
        accessToken: "",
        userDetails: {
          name: "",
          email: "",
          givenName: "",
        },
      });
      setModuleAccessToken("");
      setRoleName("");
      setIsApproved(false);
      
      // Clear session storage
      try {
        sessionStorage.clear();
      } catch (error) {
        console.error('[SessionContext] Failed to clear sessionStorage:', error);
      }
      
      // Logout from contentqaMsalInstance (module)
      try {
        await contentqaMsalInstance.initialize();
        const accounts = contentqaMsalInstance.getAllAccounts();
        if (accounts && accounts.length > 0) {
          await contentqaMsalInstance.logoutRedirect({
            account: accounts[0],
            postLogoutRedirectUri: window.location.origin,
          });
        }
      } catch (error) {
        console.error('[SessionContext] Error logging out from module MSAL:', error);
      }
      
      // Logout from platform MSAL instance
      try {
        await msalInstance.initialize();
        const accounts = msalInstance.getAllAccounts();
        if (accounts && accounts.length > 0) {
          await msalInstance.logoutRedirect({
            account: accounts[0],
            postLogoutRedirectUri: window.location.origin,
          });
        }
      } catch (error) {
        console.error('[SessionContext] Error logging out from platform MSAL:', error);
      }
    } catch (error) {
      console.error('[SessionContext] Logout error:', error);
      // Fallback: redirect to origin
      window.location.href = window.location.origin;
    }
  };

  return (
    <SessionProviderContext.Provider
      value={{
        sessionDetails,
        setSessionDetails,
        getToken,
        getModuleToken,
        getPlatformToken,
        isJoinedRoom,
        setIsJoinedRoom,
        pageContent,
        setPageContent,
        // selectedFileData,
        // setSelectedFileData,
        roleName,
        setRoleName,
        isApproved,
        setIsApproved,
        hasAccessError,
        setHasAccessError,
        moduleAccessToken,
        setModuleAccessToken,
        logout,
      }}
    >
      {children}
    </SessionProviderContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionProviderContext);
  if (!context) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
};
